<template>
  <section class="findings-table card" :data-density="density" :style="{ '--row-height': rowHeight }" aria-labelledby="findingsTitle">
    <h2 class="card-title" id="findingsTitle">
      <span class="ttl">Detected findings</span>
      <span class="scope-suffix">({{ scopeLabel }})</span>
      <span class="row-count">{{ rowCountLabel }}</span>
      <span class="spacer"></span>
      <button type="button" class="grp-toggle" :aria-pressed="String(grouped)" @click="setGrouped(!grouped)">
        <v-icon :size="15" class="grp-toggle__icon">mdi-format-list-group</v-icon>
        Group by component
      </button>
      <density-toggle :value="density" @input="$emit('update:density', $event)" />
    </h2>

    <!-- Bulk bar: appears under the title while a selection exists; drives the same decision path as the row buttons. -->
    <div v-if="canReviewAiAnalysis && selected.length" class="bulk-bar">
      <span>{{ selected.length }} selected<span v-if="selectedLinkedCount" class="bulk-mix"> · {{ selectedLinkedCount }} already linked</span></span>
      <v-btn small depressed color="primary" class="bulk-btn" :disabled="busy" @click="$emit('approve-selected', selected)">
        <v-icon small left>mdi-check</v-icon>
        Accept selected
      </v-btn>
      <v-btn small depressed color="error" class="bulk-btn" :disabled="busy" @click="$emit('reject-selected', selected)">
        <v-icon small left>mdi-close</v-icon>
        Reject selected
      </v-btn>
      <v-btn small outlined color="primary" class="bulk-btn" :disabled="busy" @click="$emit('link-selected', selected)">
        <v-icon small left>mdi-link-variant</v-icon>
        Link to component
      </v-btn>
      <v-btn v-if="selectedLinkedCount" small text color="error" class="bulk-btn" :disabled="busy" @click="$emit('unlink-selected', selected)">
        <v-icon small left>mdi-link-variant-off</v-icon>
        Remove link
      </v-btn>
      <v-btn small text class="bulk-btn" @click="$emit('update:selected', [])">Clear</v-btn>
    </div>

    <div v-if="loading && !findings.length" class="loading-block" aria-busy="true" aria-live="polite">
      <v-progress-circular indeterminate :size="28" :width="3" color="primary" />
      <span>Loading detected findings…</span>
    </div>
    <div v-else-if="!findings.length" class="state-block">
      <div class="state-block__icon"><v-icon :size="30">mdi-check-circle-outline</v-icon></div>
      <h3>No findings detected</h3>
      <p>The analysis completed without proposing any corrosion or coating-breakdown finding.</p>
    </div>

    <div v-else class="card-body" :class="{ 'card-body--with-panel': !!$slots.panel }">
      <div class="table-wrap">
        <v-progress-linear v-if="loading" indeterminate height="2" color="primary" class="table-refresh" />
        <table class="eg findings">
          <colgroup>
            <col v-for="column in columns" :key="column.key" :class="`c-${column.key}`" />
          </colgroup>
          <thead>
            <!-- One <th> per column so the label, its tooltip and its sort state come from a single list. -->
            <tr>
              <th
                v-for="column in columns"
                :key="column.key"
                :class="[column.thClass, { 'is-sortable': column.sortable, 'is-sorted': sortKey === column.key }]"
                :aria-sort="sortKey === column.key ? ariaSort : null"
                :tabindex="column.sortable ? 0 : null"
                :title="column.sortable ? sortHint(column) : null"
                @click="column.sortable && setSort(column.key)"
                @keydown.enter.prevent="column.sortable && setSort(column.key)"
                @keydown.space.prevent="column.sortable && setSort(column.key)">
                <input
                  v-if="column.key === 'select'"
                  ref="selectAll"
                  type="checkbox"
                  :checked="allSelected"
                  aria-label="Select all findings"
                  @click.stop="selectAll($event.target.checked)" />
                <template v-else>
                  <abbr v-if="column.tooltip" :title="tooltips[column.tooltip]">{{ column.label }}</abbr>
                  <template v-else>{{ column.label }}</template>
                  <v-icon v-if="sortKey === column.key" :size="12" class="sort" aria-hidden="true">{{ sortIcon }}</v-icon>
                </template>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="group in groups">
              <!-- Group by component: one row per linked component, "Not linked" last (utils/findingComponents.js). -->
              <tr
                v-if="grouped"
                :key="`grp-${group.key}`"
                class="group-row"
                :class="{ 'group-row--unlinked': group.key === unlinkedGroupKey }"
                tabindex="0"
                :data-group="group.key"
                :aria-expanded="String(!isCollapsed(group.key))"
                @click="toggleGroup(group.key)"
                @keydown.enter.prevent="toggleGroup(group.key)"
                @keydown.space.prevent="toggleGroup(group.key)">
                <td v-if="canReviewAiAnalysis" class="col-select"></td>
                <td class="col-id"><v-icon :size="18" class="chev">mdi-chevron-right</v-icon></td>
                <td class="col-location">
                  <v-icon v-if="group.type" :size="15" class="group-icon" :class="`t-${group.type}`">{{ typeIcon(group.type) }}</v-icon>
                  <span class="group-title">{{ group.name }}</span><span class="group-count">{{ group.rows.length }} {{ group.rows.length === 1 ? 'finding' : 'findings' }}</span>
                </td>
                <td class="grp-fill" :colspan="columns.length - FROZEN_COLUMNS + (canReviewAiAnalysis ? 0 : 1)"><span v-if="group.key === unlinkedGroupKey" class="group-hint">{{ unlinkedHint }}</span></td>
              </tr>
              <template v-if="!grouped || !isCollapsed(group.key)">
                <finding-row
                  v-for="finding in group.rows"
                  :key="finding.id"
                  :finding="finding"
                  :image-size="sizeById[finding.imageId] || {}"
                  :scan-name="scanNameOf(finding)"
                  :active="finding.id === activeId"
                  :selected="selectedSet.has(finding.id)"
                  @focus="$emit('focus', $event)"
                  @select="toggleSelected"
                  @approve="$emit('approve', $event)"
                  @reject="$emit('reject', $event)"
                  @details="$emit('details', $event)"
                  @link="$emit('link', $event)"
                  @unlink="$emit('unlink', $event)" />
              </template>
            </template>
          </tbody>
        </table>
      </div>
      <!-- Detail panel (Khalid, 2026-09-06: Beside) — a sibling of the table inside the card, like the Registers record panel. -->
      <slot name="panel"></slot>
    </div>
  </section>
</template>

<script>
import aiAnalysisPermissionMixin from '@/features/Dashboard/utils/aiAnalysisPermissionMixin';
  import { UNLINKED_GROUP_KEY, componentTypeIcon, groupByComponent, linkedCount } from '../../utils/findingComponents';
  import { SORT_ASC, SORT_DESC, sortFindings } from '../../utils/findingsSort';
  import { DENSITIES, isDensity } from './densityPreference';
  import { REPORT_SCOPE, RI_TOOLTIP, CAT_TOOLTIP, CONFIDENCE_TOOLTIP, AREA_TOOLTIP, SCAB_CONFIDENCE_TOOLTIP, UNLINKED_GROUP_HINT } from './reportConstants';
  import DensityToggle from './DensityToggle.vue';
  import FindingRow from './FindingRow.vue';

  const FROZEN_COLUMNS = 3; // select + ID + Location stay frozen; the group filler spans the rest

  /**
   * The table's columns in order. `sortable` covers every column that holds data - select and Actions have none.
   * `tooltip` names a key of `tooltips`; the sort rules live in utils/findingsSort.js.
   */
  const COLUMN_DEFS = Object.freeze([
    { key: 'select', label: '', sortable: false, thClass: 'col-select' },
    { key: 'id', label: 'ID', sortable: true, thClass: 'col-id' },
    { key: 'location', label: 'Location', sortable: true, thClass: 'col-location' },
    { key: 'component', label: 'Component', sortable: true, thClass: 'col-component' },
    { key: 'ri', label: 'Prov. Ri', tooltip: 'ri', sortable: true, thClass: 'num' },
    { key: 'cat', label: 'Prov. cat.', tooltip: 'cat', sortable: true, thClass: 'col-cat center' },
    { key: 'conf', label: 'Corrosion conf.', tooltip: 'confidence', sortable: true, thClass: 'center' },
    { key: 'area', label: 'Rel. area %', tooltip: 'area', sortable: true, thClass: 'num' },
    { key: 'scab', label: 'Scab measure', sortable: true, thClass: 'col-scab' },
    { key: 'scabconf', label: 'Scab conf.', tooltip: 'scabConfidence', sortable: true, thClass: 'center' },
    { key: 'notes', label: 'Notes / Reason', sortable: true, thClass: 'col-notes' },
    { key: 'actions', label: 'Actions', sortable: false, thClass: 'actions col-actions' },
  ]);

  export default {
    mixins: [aiAnalysisPermissionMixin],
    name: 'FindingsTable',
    components: { DensityToggle, FindingRow },
    props: {
      /** Badge-ordered findings (AI_ANALYSIS/FINDINGS_FOR_JOB). */
      findings: { type: Array, default: () => [] },
      /** { [imageId]: { width, height } } */
      sizeById: { type: Object, default: () => ({}) },
      /** { [imageId]: image } for scan names. */
      imagesById: { type: Object, default: () => ({}) },
      selected: { type: Array, default: () => [] },
      activeId: { type: String, default: null },
      loading: { type: Boolean, default: false },
      busy: { type: Boolean, default: false },
      density: { type: String, default: DENSITIES.DEFAULT, validator: isDensity },
    },
    data() {
      return {
        FROZEN_COLUMNS,
        grouped: false,
        collapsed: [],
        // ID ascending is the badge order the report is numbered in, so the table opens in its natural state.
        sortKey: 'id',
        sortDir: SORT_ASC,
        scopeLabel: REPORT_SCOPE.toLowerCase(),
        tooltips: { ri: RI_TOOLTIP, cat: CAT_TOOLTIP, confidence: CONFIDENCE_TOOLTIP, area: AREA_TOOLTIP, scabConfidence: SCAB_CONFIDENCE_TOOLTIP },
        unlinkedGroupKey: UNLINKED_GROUP_KEY,
        unlinkedHint: UNLINKED_GROUP_HINT,
      };
    },
    computed: {
      columns() {
        return this.canReviewAiAnalysis ? COLUMN_DEFS : COLUMN_DEFS.filter(column => column.key !== 'select');
      },
      sortIcon() {
        return this.sortDir === SORT_DESC ? 'mdi-arrow-down' : 'mdi-arrow-up';
      },
      ariaSort() {
        return this.sortDir === SORT_DESC ? 'descending' : 'ascending';
      },
      /** The findings in the current column order; grouping slices this, so groups stay sorted too. */
      sortedFindings() {
        return sortFindings(this.findings, this.sortKey, this.sortDir, this.sizeById);
      },
      rowHeight() {
        return `var(--table-row-${this.density})`;
      },
      rowCountLabel() {
        const count = this.findings.length;
        return `${count} ${count === 1 ? 'finding' : 'findings'}`;
      },
      selectedSet() {
        return new Set(this.selected);
      },
      allSelected() {
        return this.findings.length > 0 && this.selected.length === this.findings.length;
      },
      selectedLinkedCount() {
        return linkedCount(this.findings.filter((f) => this.selectedSet.has(f.id)));
      },
      /** Grouped by the linked component (name order, "Not linked" last); the column sort applies inside each group. */
      groups() {
        const rows = this.sortedFindings;
        if (!this.grouped) return [{ key: '', rows }];
        return groupByComponent(rows);
      },
    },
    watch: {
      selected: 'syncIndeterminate',
      findings: 'syncIndeterminate',
    },
    mounted() {
      this.syncIndeterminate();
    },
    methods: {
      scanNameOf(finding) {
        return (this.imagesById[finding.imageId] || {}).name || '';
      },
      typeIcon(type) {
        return componentTypeIcon(type);
      },
      syncIndeterminate() {
        this.$nextTick(() => {
          const box = this.$refs.selectAll;
          if (box) box.indeterminate = this.selected.length > 0 && this.selected.length < this.findings.length;
        });
      },
      toggleSelected({ id, checked }) {
        const next = checked ? [...new Set([...this.selected, id])] : this.selected.filter((value) => value !== id);
        this.$emit('update:selected', next);
      },
      selectAll(checked) {
        this.$emit('update:selected', checked ? this.findings.map((f) => f.id) : []);
      },
      /** Click a header to sort by it; click the active one again to flip direction. There is no third, unsorted state. */
      setSort(key) {
        if (this.sortKey === key) {
          this.sortDir = this.sortDir === SORT_ASC ? SORT_DESC : SORT_ASC;
          return;
        }
        this.sortKey = key;
        this.sortDir = SORT_ASC;
      },
      sortHint(column) {
        const label = column.label || column.key;
        if (this.sortKey !== column.key) return `Sort by ${label}`;
        const direction = this.sortDir === SORT_ASC ? 'ascending' : 'descending';
        return `${label}: ${direction}. Click to reverse.`;
      },
      setGrouped(on) {
        this.grouped = !!on;
        this.collapsed = [];
      },
      isCollapsed(name) {
        return this.collapsed.includes(name);
      },
      toggleGroup(name) {
        this.collapsed = this.collapsed.includes(name) ? this.collapsed.filter((value) => value !== name) : [...this.collapsed, name];
      },
    },
  };
</script>

<style lang="scss">
  @import './aiAnalysisTokens';
  @import './engineeringGrid';

  // Unscoped on purpose: FindingRow renders the <tr>/<td> of this table and must share these rules.
  // Every selector is rooted at .findings-table so nothing leaks.
  .findings-table {
    @include ai-analysis-tokens;
    @include engineering-grid;
    --row-height: var(--table-row-default);

    /* The card is a flex column so the body (table + optional panel) takes the height the report grid gives it. */
    display: flex;
    flex-direction: column;
    min-height: 0;
    .card-body { position: relative; display: flex; align-items: stretch; flex: 1 1 auto; min-width: 0; min-height: 0; }
    .card-body .table-wrap { flex: 1 1 auto; min-width: 0; min-height: 0; }
    /* The panel is bounded by the body (absolute, top to bottom) so it fits the available height instead of
       stretching the card past the viewer; the table keeps its lane to the left of it. */
    --panel-width: 420px; // a little wider than the 392px Registers record (Khalid, 2026-09-06)
    --panel-min-body-height: 560px;
    .card-body--with-panel { min-height: var(--panel-min-body-height); }
    .card-body--with-panel .table-wrap { margin: var(--ds-space-sm) calc(var(--panel-width) + 2 * var(--ds-space-sm)) var(--ds-space-sm) var(--ds-space-sm); }
    .card-body > .detail-panel { position: absolute; inset: var(--ds-space-sm) var(--ds-space-sm) var(--ds-space-sm) auto; margin: 0; max-height: none; }
    .table-wrap { max-height: min(72vh, 820px); border: none; border-radius: 0; }
    table.eg.findings { min-width: 1440px; }

    col.c-select { width: 40px; }
    col.c-id { width: 48px; }
    col.c-location { width: 210px; }
    col.c-component { width: 200px; }
    col.c-ri { width: 84px; }
    col.c-cat { width: 92px; }
    col.c-conf { width: 136px; }
    col.c-area { width: 108px; }
    col.c-scab { width: 126px; }
    col.c-scabconf { width: 108px; }
    col.c-notes { width: 210px; }
    col.c-actions { width: 124px; }

    table.eg thead th { padding: 6px 10px; border-top: 1px solid var(--aa-border-default); }
    table.eg tbody td { padding-right: 10px; padding-left: 10px; }
    table.eg th .sort { margin-left: 2px; color: var(--aa-brand) !important; vertical-align: -1px; }
    /* Sortable headers are controls: they need a pointer, a hover, and a focus ring that survives the sticky header. */
    table.eg thead th.is-sortable { cursor: pointer; user-select: none; }
    table.eg thead th.is-sortable:hover { background: var(--aa-surface-hover); }
    table.eg thead th.is-sortable:focus-visible { outline: 2px solid var(--v-primary-base); outline-offset: -2px; }
    table.eg thead th.is-sorted { color: var(--aa-text-primary); }

    /* Frozen identity block (select + ID + Location); Location carries the 2px separator. */
    table.eg th.col-select, table.eg td.col-select { position: sticky; left: 0; z-index: var(--z-table-sticky-col); text-align: center; padding: 0 8px; }
    table.eg th.col-id, table.eg td.col-id { position: sticky; left: 40px; z-index: var(--z-table-sticky-col); text-align: center; padding: 0 8px; }
    table.eg th.col-location, table.eg td.col-location { position: sticky; left: 88px; z-index: var(--z-table-sticky-col); box-shadow: 2px 0 0 var(--aa-border-default); }
    table.eg th.col-select, table.eg th.col-id, table.eg th.col-location { z-index: var(--z-table-sticky-corner); }
    /* Frozen Actions column on the right (Khalid, 2026-09-06): the row decisions stay reachable while the wide table scrolls. */
    table.eg th.col-actions, table.eg td.col-actions { position: sticky; right: 0; z-index: var(--z-table-sticky-col); box-shadow: -2px 0 0 var(--aa-border-default); }
    table.eg th.col-actions { z-index: var(--z-table-sticky-corner); }
    table.eg tr.group-row td.col-select, table.eg tr.group-row td.col-id, table.eg tr.group-row td.col-location { z-index: var(--z-table-group-frozen); box-shadow: none; }
    tr.group-row .group-count { margin-left: 8px; }
    tr.group-row .chev { color: var(--aa-brand) !important; transition: transform var(--ds-transition-fast); }
    tr.group-row[aria-expanded='true'] .chev { transform: rotate(90deg); }

    /* Frozen cells keep an opaque surface; hover / active / selected tints are painted on top so nothing shows through. */
    tbody tr:hover td.col-select, tbody tr:hover td.col-id, tbody tr:hover td.col-location,
    tbody tr.is-active td.col-select, tbody tr.is-active td.col-id, tbody tr.is-active td.col-location,
    tbody tr.is-selected td.col-select, tbody tr.is-selected td.col-id, tbody tr.is-selected td.col-location,
    tbody tr:hover td.col-actions, tbody tr.is-active td.col-actions, tbody tr.is-selected td.col-actions { background: var(--aa-surface); }
    tbody tr:hover td.col-select::after, tbody tr:hover td.col-id::after, tbody tr:hover td.col-location::after, tbody tr:hover td.col-actions::after { content: ''; position: absolute; inset: 0; background: var(--aa-surface-hover); pointer-events: none; }
    tbody tr.is-selected td { background: var(--aa-surface-active); }
    tbody tr.is-active td { background: var(--aa-surface-active); }
    tbody tr.is-active td.col-id { box-shadow: inset 3px 0 0 var(--aa-brand); }
    tbody tr.is-selected td.col-select::after, tbody tr.is-selected td.col-id::after, tbody tr.is-selected td.col-location::after,
    tbody tr.is-active td.col-select::after, tbody tr.is-active td.col-id::after, tbody tr.is-active td.col-location::after,
    tbody tr.is-selected td.col-actions::after, tbody tr.is-active td.col-actions::after { content: ''; position: absolute; inset: 0; background: var(--aa-surface-active); pointer-events: none; }
    tbody tr.finding-row { cursor: pointer; }
    table.eg tbody tr.is-rejected td { color: var(--aa-text-disabled); }
    tbody tr.is-rejected .loc-main { text-decoration: line-through; }
    tbody tr.is-rejected .ring { opacity: 0.45; }
    input[type='checkbox'] { width: 15px; height: 15px; accent-color: var(--aa-brand); cursor: pointer; vertical-align: middle; }

    .loc-main { font-weight: 500; overflow: hidden; text-overflow: ellipsis; }
    table.eg td.col-cat { font-family: var(--ds-font-family-mono) !important; font-size: var(--aa-grid-font-primary); font-weight: 600; }
    // Vertical padding on the reason cell: 13px text against a 0px vertical pad reads as cramped. The row height
    // is fixed on the td, so this only affects where the text sits inside it.
    table.eg td.col-notes { color: var(--aa-text-secondary); padding-top: 6px; padding-bottom: 6px; }

    .badge {
      display: inline-grid;
      place-items: center;
      width: 24px;
      height: 22px;
      border-radius: var(--ds-radius-sm);
      background: var(--aa-badge);
      color: #fff;
      font-family: var(--ds-font-family-mono) !important;
      font-size: var(--aa-grid-font-id);
      font-weight: 700;
      line-height: 1;
      font-variant-numeric: tabular-nums;
    }
    tr.is-accepted .badge { box-shadow: 0 0 0 2px var(--ds-brand-emerald); }
    tr.is-rejected .badge { background: var(--aa-text-disabled); }

    .chip {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      height: 20px;
      min-width: 74px;
      justify-content: center;
      padding: 0 8px;
      border-radius: 2px;
      font-size: var(--aa-grid-font-secondary);
      font-weight: 600;
      letter-spacing: 0.04em;
      border: 1px solid transparent;
      white-space: nowrap;
    }
    .chip--scab-no { background: rgba(3, 98, 76, 0.1); color: var(--aa-scab-no); border-color: rgba(3, 98, 76, 0.3); }
    .chip--scab-possible { background: rgba(180, 83, 9, 0.12); color: var(--aa-scab-possible); border-color: rgba(180, 83, 9, 0.32); }
    .chip--scab-yes { background: rgba(183, 28, 28, 0.1); color: var(--aa-scab-yes); border-color: rgba(183, 28, 28, 0.3); }

    /* Component cell: a chip that opens the picker, a hover-revealed ✕ that clears the link, or a quiet "Not linked" entry. */
    --type-equipment: #215f9b;
    --type-pipeline: var(--aa-brand);
    --type-structural: #6d4c9b;
    table.eg td.col-component { padding-left: 6px; text-overflow: clip; }
    .comp-wrap { display: flex; align-items: center; gap: 2px; min-width: 0; }
    .comp {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      min-width: 0;
      max-width: 100%;
      height: 22px;
      padding: 0 6px;
      border: 1px solid transparent;
      border-radius: var(--ds-radius-sm);
      background: transparent;
      color: var(--aa-text-primary);
      font: inherit;
      font-size: var(--aa-grid-font-secondary);
      font-weight: 500;
      text-align: left;
      cursor: pointer;

      &:hover { border-color: var(--aa-border-default); background: var(--aa-surface); }
      &:focus-visible { outline: 2px solid var(--aa-focus); outline-offset: 1px; }
      &__icon { flex: 0 0 auto; color: var(--aa-text-secondary) !important; }
      &__name { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-family: var(--ds-font-family-mono) !important; font-size: var(--aa-grid-font-secondary); letter-spacing: 0.01em; }
      &__caret { flex: 0 0 auto; color: var(--aa-text-disabled) !important; opacity: 0; transition: opacity var(--ds-transition-fast); }
      &__add { flex: 0 0 auto; color: var(--aa-brand) !important; opacity: 0; transition: opacity var(--ds-transition-fast); }
      &:hover .comp__caret, &:focus-visible .comp__caret, &:hover .comp__add, &:focus-visible .comp__add { opacity: 1; }
    }
    .comp--none { color: var(--aa-text-disabled); }
    .comp--none .comp__name { font-family: var(--ds-font-family-body) !important; }
    tbody tr:hover .comp__caret, tbody tr.is-active .comp__caret, tbody tr:hover .comp__add, tbody tr.is-active .comp__add { opacity: 1; }
    .comp .t-Equipment, .group-icon.t-Equipment { color: var(--type-equipment) !important; }
    .comp .t-Pipeline, .group-icon.t-Pipeline { color: var(--type-pipeline) !important; }
    .comp .t-Structural, .group-icon.t-Structural { color: var(--type-structural) !important; }
    .comp-unlink {
      flex: 0 0 auto;
      display: inline-grid;
      place-items: center;
      width: 18px;
      height: 18px;
      border: none;
      border-radius: var(--ds-radius-sm);
      background: transparent;
      color: var(--aa-text-muted);
      cursor: pointer;
      opacity: 0;
      transition: opacity var(--ds-transition-fast);

      &__icon { color: inherit !important; }
      &:hover { background: rgba(183, 28, 28, 0.1); color: var(--aa-error); }
      &:focus-visible { outline: 2px solid var(--aa-focus); outline-offset: 1px; opacity: 1; }
    }
    tbody tr:hover .comp-unlink, tbody tr.is-active .comp-unlink { opacity: 1; }
    @media (hover: none) { .comp__caret, .comp__add, .comp-unlink { opacity: 1; } }
    .group-icon { margin-right: 6px; vertical-align: -2px; }
    .group-hint { font-weight: 400; color: var(--aa-text-muted); }
    tr.group-row--unlinked .group-title { color: var(--aa-text-secondary); }

    /* Row actions (rule 06): ✓ ✕ on hover / focus / active row, ⋮ visible; the tint pseudo-elements never block the buttons (pointer-events: none). */
    .row-btn--accept { color: var(--aa-brand) !important; }
    .row-btn--reject { color: var(--aa-error) !important; }
    .decision-pill {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      height: 20px;
      padding: 0 7px;
      border-radius: 2px;
      font-size: var(--aa-grid-font-secondary);
      font-weight: 600;
      letter-spacing: 0.03em;
    }
    .decision-pill__icon { color: inherit !important; }
    .decision-pill--accepted { background: rgba(3, 98, 76, 0.12); color: var(--aa-review-approved); }
    .decision-pill--rejected { background: rgba(183, 28, 28, 0.1); color: var(--aa-error); }

    /* Card title controls */
    .row-count { font-weight: 500; color: var(--aa-text-muted); font-size: var(--ds-font-caption); font-family: var(--ds-font-family-body); }
    .grp-toggle {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      height: 28px;
      padding: 0 10px;
      border: 1px solid var(--aa-border-default);
      border-radius: var(--ds-radius-sm);
      background: var(--aa-surface);
      color: var(--aa-text-secondary);
      font-family: var(--ds-font-family-body);
      font-size: var(--ds-font-caption);
      font-weight: 500;
      cursor: pointer;

      &__icon { color: inherit !important; }
      &:focus-visible { outline: 2px solid var(--aa-focus); outline-offset: 2px; }
      &[aria-pressed='true'] { background: var(--aa-brand); border-color: var(--aa-brand); color: #fff; }
    }
    .bulk-bar {
      display: flex;
      align-items: center;
      gap: var(--ds-space-sm);
      padding: var(--ds-space-sm) var(--ds-space-md);
      background: var(--aa-surface-active);
      border-top: 1px solid var(--aa-border-subtle);
      font-size: var(--ds-font-body-sm);
    }

    /* states (same blocks as the jobs table) */
    .loading-block { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: var(--ds-space-sm); padding: 48px var(--ds-space-md); color: var(--aa-text-secondary); font-size: var(--ds-font-body-sm); border-top: 1px solid var(--aa-border-subtle); }
    .state-block { padding: var(--ds-space-xl); text-align: center; border-top: 1px solid var(--aa-border-subtle); }
    .state-block h3 { font-size: var(--ds-font-subtitle); font-weight: 600; margin: 0 0 var(--ds-space-xs); }
    .state-block p { margin: 0 auto; max-width: 460px; font-size: var(--ds-font-body-sm); color: var(--aa-text-muted); line-height: 1.6; }
    .state-block__icon { display: inline-flex; align-items: center; justify-content: center; width: 56px; height: 56px; margin-bottom: var(--ds-space-md); border-radius: 50%; background: var(--aa-surface-subtle); .v-icon { color: var(--aa-text-disabled); } }
  }

  .theme--dark .findings-table {
    @include ai-analysis-tokens-dark;
    --type-equipment: #7fb3e6;
    --type-structural: #b79cdc;
  }
</style>
